import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Heading,
  ButtonGroup,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { MaskedInput } from '../Form/MaskedInput';

interface IConfirmationFormData {
  reason?: string;
}

const confirmationFormSchema = (
  isReasonRequired: boolean,
  reasonAlert?: string,
): Yup.AnyObjectSchema =>
  Yup.object().shape({
    reason: Yup.string()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' ? null : value,
      )
      .when([], {
        is: () => isReasonRequired,
        then: (schema) => schema.required(reasonAlert),
        otherwise: (schema) => schema.notRequired(),
      }),
  });

interface IConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => void;
  title?: string;
  message: string;
  reasonAlert?: string;
  isReasonRequired?: boolean;
  showReasonInput?: boolean;
  warningMessage?: string;
  isConfirming?: boolean;
}

export const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirmação de ação',
  message,
  reasonAlert,
  isReasonRequired = false,
  showReasonInput = false,
  warningMessage,
  isConfirming = false,
}: IConfirmationModalProps): JSX.Element => {
  const { register, formState, handleSubmit, reset } = useForm({
    resolver: yupResolver(
      confirmationFormSchema(isReasonRequired, reasonAlert),
    ),
  });

  const { errors } = formState;

  const handleClose = (): void => {
    reset({ reason: null });

    onClose();
  };

  const handleConfirmationSubmit: SubmitHandler<IConfirmationFormData> = ({
    reason,
  }) => {
    onConfirm(reason);
  };

  return (
    <Modal
      size="lg"
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={!isConfirming}
      closeOnEsc={!isConfirming}
    >
      <ModalOverlay />

      <ModalContent as="form" onSubmit={handleSubmit(handleConfirmationSubmit)}>
        <ModalHeader>
          <Heading size="lg" fontWeight="normal">
            {title}
          </Heading>
        </ModalHeader>

        <ModalCloseButton isDisabled={isConfirming} />

        <ModalBody>
          <Text>{message}</Text>

          <VStack mt="4">
            <Text color="red.500" fontSize="sm">
              {warningMessage}
            </Text>

            {!!showReasonInput && (
              <MaskedInput
                label="Motivo"
                as="textarea"
                minHeight="160px"
                resize="none"
                py="2"
                error={errors.reason}
                {...register('reason')}
              />
            )}
          </VStack>
        </ModalBody>

        <ModalFooter mt="14">
          <ButtonGroup>
            <Button
              colorScheme="blackAlpha"
              onClick={handleClose}
              isDisabled={isConfirming}
            >
              Cancelar
            </Button>

            <Button colorScheme="green" type="submit" isLoading={isConfirming}>
              Confirmar
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
